import { Icon } from "@mdi/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  AddLocation,
  Delete,
  DeleteOutline,
  Functions,
  GridOn,
  KeyboardArrowDown
} from "@material-ui/icons";
import { mdiPlaylistEdit } from "@mdi/js";
import { Button, Popconfirm } from "antd";
import classnames from "classnames";
import { requestAddNewField as addField } from "store/features";
import { RootState } from "store/rootReducer";
import styled from "styled-components";

import { useUser } from "hooks";

import { EntityKind } from "models/entityKind";

import { Popover, Tooltip } from "components/base";
import { useGeomBinDispatch } from "components/geom-bin/hooks/useGeomBinDispatch";

import { useGroupByStore } from "./GroupByStoreProvider";
import {
  selectAllColumns,
  selectCheckedFields,
  selectCustomFieldsEditToggle,
  selectEntityKind,
  selectProps,
  selectSelectedCategory,
  selectSelectedTab
} from "./groupBySelectors";
import { getUniqueFieldGroups } from "./helpers";
import {
  useMutationFieldDelete,
  useMutationGeomBinDelete,
  useSelectedCategory
} from "./hooks";
import { useProjectPermissions } from "./hooks/useProjectPermissionsContext";

/* eslint-disable @typescript-eslint/no-explicit-any */
interface UserFieldToolbarProps {
  className?: string;
  toolbarActive?: boolean;
  toolbarFocusedIndex?: Maybe<number>;
  onSelectRef?: (index: number, selectEl: Maybe<any>) => void;
  isDisablingManagement?: boolean;
}

function UserFieldToolbar({
  className = "",
  toolbarActive = false,
  toolbarFocusedIndex = null,
  onSelectRef,
  isDisablingManagement = false
}: UserFieldToolbarProps = {}) {
  const dispatch = useDispatch();
  const allColumns = useGroupByStore(selectAllColumns);
  const checkedFields = useGroupByStore(selectCheckedFields);
  const customFieldsEditToggle = useGroupByStore(selectCustomFieldsEditToggle);
  const selectedTab = useGroupByStore(selectSelectedTab);
  const entityKind = useGroupByStore(selectEntityKind);
  const selectedCategory = useGroupByStore(selectSelectedCategory);
  const setCustomFieldsEditToggle = useGroupByStore((s) => s.setCustomFieldsEditToggle);
  const selectedProjectId = useSelector(
    (state: RootState) => state.groupBy.selectedProjectIdForColumns
  );
  const { isAtLeastPowerUser } = useUser();
  const { isOrgDefinedColumn, isUserDefinedColumn, isProjectDefinedColumn } =
    useSelectedCategory();

  // Collect unique subgroups from checked fields
  const checkedFieldGroups = useMemo(
    () => getUniqueFieldGroups(allColumns, [...checkedFields]),
    [allColumns, checkedFields]
  );

  // Property keys (distinct from the `id`s used above) of the checked
  // fields, so their entries can be purged from saved defaults on delete.
  const checkedFieldProperties = useMemo(
    () =>
      allColumns
        .filter((column) => checkedFields.includes(column.id))
        .map((column) => column.property),
    [allColumns, checkedFields]
  );

  const { mutate: fieldDelete } = useMutationFieldDelete({
    ids: [...checkedFields],
    isOrg: isOrgDefinedColumn,
    entityKind,
    isProjectColumn: isProjectDefinedColumn,
    fieldGroups: checkedFieldGroups,
    properties: checkedFieldProperties
  });

  const { mutate: geomFieldDelete } = useMutationGeomBinDelete(
    checkedFields,
    isProjectDefinedColumn ? selectedProjectId : undefined,
    checkedFieldProperties
  );
  const props = useGroupByStore(selectProps);

  const toggleEditMode = () => {
    setCustomFieldsEditToggle(!customFieldsEditToggle);
  };
  const [overflowVisible, setOverflowVisible] = useState(false);

  // Create refs for buttons that can be focused
  const deleteButtonRef = useRef<Maybe<any>>(null);
  const editToggleButtonRef = useRef<Maybe<any>>(null);
  const addButtonRef = useRef<Maybe<any>>(null);

  // Report button refs to parent when they change
  useEffect(() => {
    if (onSelectRef) {
      const buttons = [];
      if (customFieldsEditToggle && deleteButtonRef.current) {
        buttons.push(deleteButtonRef.current);
      }
      if (editToggleButtonRef.current) {
        buttons.push(editToggleButtonRef.current);
      }
      if (addButtonRef.current) {
        buttons.push(addButtonRef.current);
      }

      buttons.forEach((button, index) => {
        onSelectRef(index, button);
      });
    }
  }, [
    onSelectRef,
    customFieldsEditToggle,
    deleteButtonRef.current,
    editToggleButtonRef.current,
    addButtonRef.current
  ]);

  // derived state
  const isOrgAdmin = isAtLeastPowerUser && isOrgDefinedColumn;
  const { permissions: projectPermissions } = useProjectPermissions();

  const hasProjectEditPermission = projectPermissions?.canEditProjectItems ?? false;
  const isProjectEditable = isProjectDefinedColumn && hasProjectEditPermission;
  const isActionAllowed =
    !isDisablingManagement &&
    (((isOrgAdmin || isUserDefinedColumn) && props?.editable) || isProjectEditable);

  // if field addition allowed, derive field type: true org, false user
  const isOrg = isUserDefinedColumn || isProjectDefinedColumn ? false : true;

  const geomBinDispatch = useGeomBinDispatch();

  const handleDelete = () => {
    if (selectedTab === "fields") {
      fieldDelete();
    } else {
      geomFieldDelete();
    }
  };

  const openGeomTool = () => {
    setOverflowVisible(false);
    geomBinDispatch({
      payload: {
        isActive: true,
        selectedGeomBinId: undefined,
        isOrg,
        projectId: isProjectDefinedColumn ? selectedProjectId : undefined
      }
    });
  };

  const shouldShowComponent =
    isOrgDefinedColumn || isUserDefinedColumn || isProjectDefinedColumn;

  if (!shouldShowComponent) return null;

  return (
    <Wrapper className={className}>
      {customFieldsEditToggle && (
        <Popconfirm
          placement="bottom"
          onConfirm={(evt) => {
            handleDelete();
            evt.stopPropagation();
            evt.preventDefault();
          }}
          onCancel={(evt) => {
            evt.stopPropagation();
            evt.preventDefault();
          }}
          okText="Delete"
          okType="danger"
          title={`Are you sure you want to delete the selected fields?`}>
          <Tooltip placement="top" title="Delete checked items">
            <StyledButton
              ref={deleteButtonRef}
              className={classnames("toolbar-item", {
                "keyboard-focused": toolbarActive && toolbarFocusedIndex === 0
              })}
              danger
              disabled={!(checkedFields.length > 0) || !isActionAllowed}
              onClick={(evt) => {
                evt.stopPropagation();
                evt.preventDefault();
              }}>
              {checkedFields.length > 0 ? (
                <Delete fontSize="large" />
              ) : (
                <DeleteOutline fontSize="large" />
              )}
            </StyledButton>
          </Tooltip>
        </Popconfirm>
      )}
      {(isUserDefinedColumn || isOrgDefinedColumn || isProjectDefinedColumn) && (
        <Tooltip title={"Toggle List Edit"}>
          <StyledButton
            ref={editToggleButtonRef}
            className={classnames("toolbar-button", {
              "keyboard-focused":
                toolbarActive && toolbarFocusedIndex === (customFieldsEditToggle ? 1 : 0)
            })}
            customFieldsEditToggle={customFieldsEditToggle}
            disabled={!isActionAllowed}
            onClick={toggleEditMode}>
            <Icon
              style={{
                marginLeft: "-8px"
              }}
              path={mdiPlaylistEdit}
              className="activity-action-icon"
              size={1.4}
            />
          </StyledButton>
        </Tooltip>
      )}

      <Popover
        arrowPointAtCenter
        content={
          <OverflowWrapper>
            <Tooltip title={"Add Calculated Field"}>
              <StyledButton
                className={"toolbar-button"}
                disabled={!isActionAllowed}
                onClick={() => {
                  dispatch(
                    addField({
                      type: "Calculated",
                      isOrg,
                      entityKind,
                      projectId: isProjectDefinedColumn ? selectedProjectId : null,
                      categoryName: selectedCategory?.name
                    })
                  );
                }}>
                {<Functions fontSize="large" />}
                {"Calculated Field"}
              </StyledButton>
            </Tooltip>
            <Tooltip title={"Excel Fields"}>
              <StyledButton
                className={"toolbar-button"}
                disabled={!isActionAllowed}
                onClick={() => {
                  dispatch(
                    addField({
                      type: "Excel",
                      isOrg,
                      entityKind,
                      projectId: isProjectDefinedColumn ? selectedProjectId : null
                    })
                  );
                }}>
                {<GridOn fontSize="large" />}
                {"Excel Fields"}
              </StyledButton>
            </Tooltip>
            {entityKind == EntityKind.Well && (
              <Tooltip title={"Add Polygon Bins"}>
                <StyledButton
                  disabled={!isActionAllowed}
                  className={"toolbar-button"}
                  onClick={openGeomTool}>
                  {<AddLocation fontSize="large" />}
                  {"Polygon Bins"}
                </StyledButton>
              </Tooltip>
            )}
          </OverflowWrapper>
        }
        placement="bottomLeft"
        trigger="click"
        open={overflowVisible}
        onOpenChange={(v) => setOverflowVisible(v)}>
        <Button
          ref={(el) => {
            addButtonRef.current = el;
          }}
          disabled={!isActionAllowed}
          className={classnames("toolbar-item", {
            "keyboard-focused":
              toolbarActive && toolbarFocusedIndex === (customFieldsEditToggle ? 2 : 1)
          })}>
          {"Add"}
          <KeyboardArrowDown fontSize="large" />
        </Button>
      </Popover>
    </Wrapper>
  );
}

export default UserFieldToolbar;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 var(--space-4);
  gap: 5px;

  .ant-btn {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;
const OverflowWrapper = styled.div`
  display: grid;
  max-height: 300px;
  border-radius: var(--border-radius);
  padding: 8px 12px;
  margin: -12px -16px;
  overflow: hidden;

  & > button {
    width: auto !important;
  }
`;

const StyledButton = styled.button`
  width: 32px;
  height: 32px;
  display: grid;
  grid-template-columns: 36px max-content;
  gap: 6px;
  align-items: center;
  justify-items: center;
  border-radius: 3px;
  background: ${() => "transparent"};
  color: ${() => "#a2aaad"};
  &:hover {
    color: ${(props) => (props.danger ? "var(--color-danger)" : "var(--color-primary)")};
    background: ${() => "transparent"};
  }
  border: none;
  font-weight: 500;
  padding: 0;
  margin: 0;
  transition: color 0.15s ease-out;
  overflow: hidden;
  pointer-events: all;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    pointer-events: none;
  }

  ${({ customFieldsEditToggle }) =>
    customFieldsEditToggle && `color: var(--color-primary);`}
`;
